/**
 * Documenten bij een boot: CE-verklaringen, schema's, handleidingen.
 *
 * De bestanden gaan naar Documenten/<letter>/<klant>/<bootnaam>/ op de server;
 * hier sturen we alleen het bestand plus wat het is.
 */
import apiFetch, { getApiBaseUrl, getToken } from './api';

export interface BootDocument {
  id: number;
  boot_id: number;
  document_type: string;
  titel?: string;
  bestand_pad: string;
  taal?: string;
  versie?: string;
  omschrijving?: string;
  actief: boolean;
  zichtbaar_portaal: boolean;
  created_at?: string;
  updated_at?: string;
}

/* Vaste lijst met ruimte voor eigen invoer - zie het veld in BotenForm. */
export const DOCUMENT_TYPES = [
  'CE-verklaring',
  'Technisch dossier',
  'Elektraschema',
  'Stabiliteitsberekening',
  'Handleiding',
  'Certificaat',
  'Tekening',
  'Werkbestanden',
  'Financieel',
  'Overig',
];

export const bootDocumentenService = {
  async getByBoot(bootId: number): Promise<BootDocument[]> {
    return apiFetch(`/api/v1/boot-documenten?boot_id=${bootId}`);
  },

  async upload(
    bootId: number,
    bestand: File,
    gegevens: { document_type: string; titel?: string; taal?: string; versie?: string; omschrijving?: string; zichtbaar_portaal?: boolean }
  ): Promise<BootDocument> {
    const formData = new FormData();
    formData.append('boot_id', bootId.toString());
    formData.append('document_type', gegevens.document_type);
    if (gegevens.titel) formData.append('titel', gegevens.titel);
    if (gegevens.taal) formData.append('taal', gegevens.taal);
    if (gegevens.versie) formData.append('versie', gegevens.versie);
    if (gegevens.omschrijving) formData.append('omschrijving', gegevens.omschrijving);
    formData.append('zichtbaar_portaal', String(gegevens.zichtbaar_portaal ?? false));
    formData.append('file', bestand);

    return apiFetch('/api/v1/boot-documenten', {
      method: 'POST',
      body: formData,
      headers: {}, // laat de browser zelf de Content-Type met boundary zetten
    });
  },

  /** Zet het vinkje "zichtbaar in klantportaal" om. */
  async zetZichtbaar(documentId: number, zichtbaar: boolean): Promise<BootDocument> {
    return apiFetch(`/api/v1/boot-documenten/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify({ zichtbaar_portaal: zichtbaar }),
    });
  },

  async verwijder(documentId: number, verwijderBestand = false): Promise<void> {
    return apiFetch(
      `/api/v1/boot-documenten/${documentId}?verwijder_bestand=${verwijderBestand}`,
      { method: 'DELETE' }
    );
  },

  /** Openen in een nieuw tabblad; de download-endpoint wil een token in de header,
   *  dus halen we het bestand op als blob en openen dat. */
  async open(documentId: number): Promise<void> {
    const res = await fetch(
      `${getApiBaseUrl()}/api/v1/boot-documenten/${documentId}/download?inline=true`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    if (!res.ok) throw new Error('Kon het document niet openen');
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
};

export default bootDocumentenService;
